import type { ErrorReportInput } from '@/lib/telemetry';

const RECOVERY_BUILD_KEY = 'atrivial:vite-preload-recovery-build';
const RELOAD_TIMEOUT_MS = 10_000;
const RECOVERY_SOURCE = 'VitePreloadErrorRecovery';

// One recovery attempt belongs to the current document, including any other
// lazy imports that fail before the browser finishes navigating away.
let reloadPending = false;
const recoveryListeners = new Set<() => void>();

export function isVitePreloadReloadPending(): boolean {
  return reloadPending;
}

export function subscribeToVitePreloadRecovery(listener: () => void): () => void {
  recoveryListeners.add(listener);
  return () => {
    recoveryListeners.delete(listener);
  };
}

function setReloadPending(pending: boolean): void {
  reloadPending = pending;
  recoveryListeners.forEach((listener) => listener());
}

interface PreloadErrorTarget {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

interface RecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface VitePreloadErrorEvent extends Event {
  payload?: unknown;
}

interface InstallVitePreloadErrorRecoveryOptions {
  target: PreloadErrorTarget;
  storage: RecoveryStorage;
  buildId: string;
  reload: () => void;
  report: (input: ErrorReportInput) => void;
}

export function installVitePreloadErrorRecovery({
  target,
  storage,
  buildId,
  reload,
  report,
}: InstallVitePreloadErrorRecoveryOptions): () => void {
  let reloadTimeout: ReturnType<typeof setTimeout> | undefined;
  const finishRecovery = () => {
    clearTimeout(reloadTimeout);
    setReloadPending(false);
  };

  const listener: EventListener = (event) => {
    const preloadEvent = event as VitePreloadErrorEvent;
    // Cancelling this event makes Vite resolve failed imports with undefined.
    // Preserve rejection so React.lazy reaches the boundary with the original error.
    if (reloadPending) {
      reportRecovery('skip', 'reload_already_pending', preloadEvent.payload);
      return;
    }

    try {
      if (storage.getItem(RECOVERY_BUILD_KEY) === buildId) {
        reportRecovery('skip', 'already_attempted_for_build', preloadEvent.payload);
        return;
      }
      storage.setItem(RECOVERY_BUILD_KEY, buildId);
    } catch {
      // Without a persistent guard, reloading could loop forever.
      reportRecovery('skip', 'session_storage_unavailable', preloadEvent.payload);
      return;
    }

    setReloadPending(true);
    reloadTimeout = setTimeout(finishRecovery, RELOAD_TIMEOUT_MS);
    reportRecovery('reload', 'new_build', preloadEvent.payload);
    try {
      reload();
    } catch (error) {
      finishRecovery();
      reportRecovery('skip', 'reload_failed', error);
    }
  };

  function reportRecovery(action: 'reload' | 'skip', reason: string, error: unknown): void {
    report({
      error,
      source: `${RECOVERY_SOURCE}:${action}:${reason}`,
      severity: 'warning',
      context: { action, buildId, recoveryReason: reason },
    });
  }

  target.addEventListener('vite:preloadError', listener);
  return () => {
    target.removeEventListener('vite:preloadError', listener);
    finishRecovery();
  };
}
